import {
  apiDate,
  boolean,
  number,
  optional,
  optionalNumber,
  optionalPositiveInteger,
  optionalString,
  optionalUuid,
  string,
  uuid,
  zenum,
  zodLess,
  ZodLessInfer,
} from '@kaiizen/zod-less';
import {
  ApiJobProgressSchema,
  JobLifecycleStatusSchema,
} from './RecordTypes/ApiBackgroundJobSchema';
import { optionalApiDate } from './schemaLib';

/**
 * Ordered, user-facing phases the `VideoToScopeJob` reports through `reportProgress`.
 * Single source of truth shared by the worker (which reports each stage) and the renovator
 * upload modal (which renders every phase so pending rows show before the job reaches them),
 * so the two can't drift as stages are added or reordered. A stage's 1-based position in this
 * array is its `stepIndex`; `totalSteps` is the array length.
 */
export const VIDEO_TO_SCOPE_JOB_PHASES = [
  { phase: 'client-setup', message: 'Connecting to 3rd-party services' },
  { phase: 'template-frames-audio', message: 'Extracting audio and examining video' },
  {
    phase: 'prelim-ai-analysis',
    message: 'Performing preliminary AI analysis of video and audio content',
  },
  {
    phase: 'clarify-transcript',
    message: 'Clarifying the audio transcript',
  },
  { phase: 'walk-events', message: 'Detecting which tasks to perform' },
  { phase: 'walk-event-times', message: 'Estimating task timestamps' },
  {
    phase: 'lineitem-and-photos',
    message: 'Matching line items to walk events',
  },
  {
    phase: 'upload-photos',
    message: 'Uploading photos to scan',
  },
  { phase: 'save-scan', message: 'Updating scan' },
] as const;

export type VideoToScopeJobPhase = (typeof VIDEO_TO_SCOPE_JOB_PHASES)[number]['phase'];

/**
 * Ordered, user-facing phases the `VideoFrameMatchFinderJob` reports through `reportProgress`.
 * Single source of truth shared by the worker (which reports each stage) and the renovator
 * upload modal (which renders every phase so pending rows show before the job reaches them),
 * so the two can't drift as stages are added or reordered. A stage's 1-based position in this
 * array is its `stepIndex`; `totalSteps` is the array length.
 */
export const VIDEO_FRAME_MATCH_FINDER_JOB_PHASES = [
  { phase: 'client-setup', message: 'Connecting to 3rd-party services' },
  { phase: 'template-frames', message: 'Examining video' },
  { phase: 'load-ref-image', message: 'Loading reference image' },
  { phase: 'match-fullvid', message: 'Scanning the full video for the closest matching frame' },
  { phase: 'match-timeslice', message: 'Honing in on a fine time-slice' },
  { phase: 'save-scan', message: 'Updating scan' },
] as const;

export type VideoFrameMatchFinderJobPhase = (typeof VIDEO_FRAME_MATCH_FINDER_JOB_PHASES)[number]['phase'];

/**
 * Ordered, user-facing phases the `VideoGroupImageCaptureJob` reports through `reportProgress`.
 */
export const VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES = [
  { phase: 'client-setup', message: 'Connecting to 3rd-party services' },
  { phase: 'template-frames-audio', message: 'Extracting audio and examining video' },
  {
    phase: 'prelim-ai-analysis',
    message: "Transcribing the video's audio",
  },
  {
    phase: 'clarify-transcript',
    message: 'Clarifying the audio transcript',
  },
  { phase: 'walk-events', message: 'Detecting rooms and areas in walkthrough' },
  { phase: 'pick-photos', message: 'Selecting the best photo for each room' },
  { phase: 'upload-photos', message: 'Uploading photos to scan' },
  { phase: 'save-scan', message: 'Updating scan' },
] as const;

export type VideoGroupImageCaptureJobPhase = (typeof VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES)[number]['phase'];

/**
 * Ordered, user-facing phases the `AudioTranscribeJob` reports through `reportProgress`.
 * Single source of truth for that operation's typed progress: the worker reports each `phase`
 * string verbatim and this catalog backs the `AudioTranscribeJob` member of the Operation union
 * ({@link ../RecordTypes/ApiOperationSchema}) via `OPERATION_PROGRESS_CATALOGS`. Adding or
 * reordering a phase here must stay in lockstep with the `reportProgress` calls in
 * `AudioTranscribeJob.execute`.
 */
export const AUDIO_TRANSCRIBE_JOB_PHASES = [
  { phase: 'transcribe-start', message: 'Starting AWS Transcribe' },
  { phase: 'transcribe-wait', message: 'Waiting for AWS Transcribe' },
  { phase: 'transcribe-write', message: 'Writing transcript' },
] as const;

export type AudioTranscribeJobPhase = (typeof AUDIO_TRANSCRIBE_JOB_PHASES)[number]['phase'];

/**
 * Ordered phase catalog for `ApplyScanVideoAudioToScopeJob` — shared so the worker's progress
 * reporter and any client cannot drift. This job starts from an already-persisted
 * `ScanVideoAudio.text` (no audio extract, no transcription), so its phases are just the scope
 * tail: load, extract scope changes for the one Group, match line items, save.
 */
export const APPLY_SCAN_VIDEO_AUDIO_TO_SCOPE_JOB_PHASES = [
  { phase: 'load-context', message: 'Loading transcript and scan' },
  { phase: 'walk-events', message: 'Identifying scope changes' },
  { phase: 'line-items', message: 'Matching line items' },
  { phase: 'save-scan', message: 'Finished applying changes' },
] as const;

export type ApplyScanVideoAudioToScopeJobPhase = (typeof APPLY_SCAN_VIDEO_AUDIO_TO_SCOPE_JOB_PHASES)[number]['phase'];

export const ApiScanVideoSubmissionSchema = zodLess({
  id: uuid,
  orgId: uuid,
  propertyProjectId: uuid,
  scanId: uuid,
  userId: uuid,
  s3Key: string,
  originalFileName: string,
  contentType: string,
  sizeBytes: number,
  playbackUrl: optionalString,
  userName: optionalString,
  orgName: optionalString,
  projectName: optionalString,
  createdAt: apiDate,
  updatedAt: apiDate,
});
export type ApiScanVideoSubmission = ZodLessInfer<
  typeof ApiScanVideoSubmissionSchema
>;

export const ApiScanVideoProgressSchema = zodLess({
  videoId: uuid,
  lifecycleStatus: optional(JobLifecycleStatusSchema.nullable()),
  progress: optional(ApiJobProgressSchema.nullable()),
  error: optionalString,
});
export type ApiScanVideoProgress = ZodLessInfer<
  typeof ApiScanVideoProgressSchema
>;

export const ApiScanVideoActiveWorkPhaseSchema = zenum('analyzing');
export type ApiScanVideoActiveWorkPhase = ZodLessInfer<
  typeof ApiScanVideoActiveWorkPhaseSchema
>;

export const ApiScanVideoActiveWorkItemSchema = zodLess({
  videoId: uuid,
  originalFileName: string,
  contentType: string,
  sizeBytes: number,
  phase: ApiScanVideoActiveWorkPhaseSchema,
  lifecycleStatus: optional(JobLifecycleStatusSchema.nullable()),
  progress: optional(ApiJobProgressSchema.nullable()),
});
export type ApiScanVideoActiveWorkItem = ZodLessInfer<
  typeof ApiScanVideoActiveWorkItemSchema
>;

export const ApiScanVideoActiveWorkListQuerySchema = zodLess({
  scanId: uuid,
  userId: uuid.optional(),
});
export type ApiScanVideoActiveWorkListQuery = ZodLessInfer<
  typeof ApiScanVideoActiveWorkListQuerySchema
>;

export const ApiScanVideoActiveWorkListResponseSchema = zodLess({
  items: [ApiScanVideoActiveWorkItemSchema],
});
export type ApiScanVideoActiveWorkListResponse = ZodLessInfer<
  typeof ApiScanVideoActiveWorkListResponseSchema
>;

/** How the server resolved init for a scan video (dedupe / reuse). */
export const ApiScanVideoUploadInitActionSchema = zenum(
  'new',
  'resumeUpload',
  'resumeMultipart',
  'retryAnalysis',
  'analysisInProgress',
  'alreadyProcessed',
);
export type ApiScanVideoUploadInitAction = ZodLessInfer<
  typeof ApiScanVideoUploadInitActionSchema
>;

export const ApiInitScanVideoUploadSchema = zodLess({
  /**
   * Client-chosen ScanVideo id. A device that records offline picks the video's
   * id when recording starts (snapshots point at it before any upload), then
   * sends it here. Re-init with the same id resumes or reports that upload. Omit it to
   * let the server choose.
   */
  id: optionalUuid,
  scanId: uuid,
  fileName: string,
  contentType: string,
  sizeBytes: number,
  contentHash: optionalString,
});
export type ApiInitScanVideoUpload = ZodLessInfer<
  typeof ApiInitScanVideoUploadSchema
>;

export const ApiInitScanVideoUploadResponseSchema = zodLess({
  action: ApiScanVideoUploadInitActionSchema,
  videoId: uuid,
  objectKey: string,
  skipUpload: boolean,
  uploadUrl: optionalString,
  expiresAt: optional(apiDate),
});
export type ApiInitScanVideoUploadResponse = ZodLessInfer<
  typeof ApiInitScanVideoUploadResponseSchema
>;

export const ApiCompleteScanVideoUploadSchema = zodLess({
  videoId: uuid,
  objectKey: string,
  sizeBytes: number,
  durationSeconds: optionalNumber,
  width: optionalPositiveInteger,
  height: optionalPositiveInteger,
  videoRecordedAt: optionalApiDate,
});
export type ApiCompleteScanVideoUpload = ZodLessInfer<
  typeof ApiCompleteScanVideoUploadSchema
>;

export const ApiCompleteScanVideoUploadResponseSchema = zodLess({
  ok: boolean,
  videoId: uuid,
});
export type ApiCompleteScanVideoUploadResponse = ZodLessInfer<
  typeof ApiCompleteScanVideoUploadResponseSchema
>;

// *************************************************************
// S3 multipart upload (large >=1GB walkthroughs)
//
// New endpoints alongside the single-part init/complete above. S3 ListParts is the
// source of truth for which parts have landed, so the server persists only the
// uploadId + part size; the client uploads missing parts and reports their ETags.
// *************************************************************

/** A presigned PUT URL for one part of an in-progress multipart upload. */
export const ApiScanVideoMultipartPartUrlSchema = zodLess({
  partNumber: number,
  url: string,
  expiresAt: apiDate,
});
export type ApiScanVideoMultipartPartUrl = ZodLessInfer<
  typeof ApiScanVideoMultipartPartUrlSchema
>;

export const ApiInitScanVideoMultipartUploadSchema = zodLess({
  /**
   * Client-chosen ScanVideo id. A device that records offline picks the video's
   * id when recording starts (snapshots point at it before any upload), then
   * sends it here. Re-init with the same id resumes or reports that upload. Omit it to
   * let the server choose.
   */
  id: optionalUuid,
  scanId: uuid,
  fileName: string,
  contentType: string,
  sizeBytes: number,
  contentHash: optionalString,
});
export type ApiInitScanVideoMultipartUpload = ZodLessInfer<
  typeof ApiInitScanVideoMultipartUploadSchema
>;

export const ApiInitScanVideoMultipartUploadResponseSchema = zodLess({
  action: ApiScanVideoUploadInitActionSchema,
  videoId: uuid,
  objectKey: string,
  skipUpload: boolean,
  uploadId: optionalString,
  partSizeBytes: optional(number),
  totalParts: optional(number),
  /** Part numbers already stored in S3 (from ListParts); the client uploads only the gap. */
  alreadyUploadedParts: [number],
  /** First batch of presigned part URLs for the missing parts (more via sign-parts). */
  parts: [ApiScanVideoMultipartPartUrlSchema],
});
export type ApiInitScanVideoMultipartUploadResponse = ZodLessInfer<
  typeof ApiInitScanVideoMultipartUploadResponseSchema
>;

export const ApiSignScanVideoUploadPartsSchema = zodLess({
  videoId: uuid,
  uploadId: string,
  partNumbers: [number],
});
export type ApiSignScanVideoUploadParts = ZodLessInfer<
  typeof ApiSignScanVideoUploadPartsSchema
>;

export const ApiSignScanVideoUploadPartsResponseSchema = zodLess({
  parts: [ApiScanVideoMultipartPartUrlSchema],
});
export type ApiSignScanVideoUploadPartsResponse = ZodLessInfer<
  typeof ApiSignScanVideoUploadPartsResponseSchema
>;

export const ApiScanVideoMultipartCompletedPartSchema = zodLess({
  partNumber: number,
  eTag: string,
});
export type ApiScanVideoMultipartCompletedPart = ZodLessInfer<
  typeof ApiScanVideoMultipartCompletedPartSchema
>;

export const ApiCompleteScanVideoMultipartUploadSchema = zodLess({
  videoId: uuid,
  objectKey: string,
  uploadId: string,
  sizeBytes: number,
  parts: [ApiScanVideoMultipartCompletedPartSchema],
  // Client-read video metadata, mirroring the single-part complete so multipart records it too.
  durationSeconds: optionalNumber,
  width: optionalPositiveInteger,
  height: optionalPositiveInteger,
  videoRecordedAt: optionalApiDate,
});
export type ApiCompleteScanVideoMultipartUpload = ZodLessInfer<
  typeof ApiCompleteScanVideoMultipartUploadSchema
>;

export const ApiAbortScanVideoMultipartUploadSchema = zodLess({
  videoId: uuid,
  uploadId: string,
});
export type ApiAbortScanVideoMultipartUpload = ZodLessInfer<
  typeof ApiAbortScanVideoMultipartUploadSchema
>;

export const ApiAbortScanVideoMultipartUploadResponseSchema = zodLess({
  ok: boolean,
  videoId: uuid,
});
export type ApiAbortScanVideoMultipartUploadResponse = ZodLessInfer<
  typeof ApiAbortScanVideoMultipartUploadResponseSchema
>;

export const ApiScanVideoMultipartStatusResponseSchema = zodLess({
  videoId: uuid,
  uploadId: optionalString,
  partSizeBytes: optional(number),
  /** Part numbers currently stored in S3 for the live upload (empty when none/completed). */
  uploadedParts: [number],
  /** True when the final object exists (upload already completed). */
  complete: boolean,
});
export type ApiScanVideoMultipartStatusResponse = ZodLessInfer<
  typeof ApiScanVideoMultipartStatusResponseSchema
>;
